// Work items — the data layer behind a project's page (spec §6.2): the types,
// one hook that loads a project's work items, workflow, categories and team and
// keeps them live, and the four writes, routed by whether the project is Jira's.
//
// The rules are the database's (20261016100000_issues_core.sql): a lead or
// member whose role has Projects edit creates and edits, the project's lead
// deletes, a viewer reads. `rights` mirrors that to hide what would be refused;
// it grants nothing. A Jira-linked project is written only through the
// jira-edit Edge Function (spec §5), and only once JIRA_WRITE is on.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth';
import { can } from './roles';
import { write } from './write';
import { JIRA_WRITE } from './jiraWrite';

export type StatusCategory = 'todo' | 'progress' | 'done';
export type IssueType = 'epic' | 'story' | 'task' | 'bug' | 'subtask';
export const ISSUE_TYPES: readonly IssueType[] = ['epic', 'story', 'task', 'bug', 'subtask'];
/** Jira's five, highest first. Mirrored rows may carry a site's own. */
export const PRIORITIES = ['Highest', 'High', 'Medium', 'Low', 'Lowest'] as const;

export type IssueStatus = {
  id: string;
  name: string;
  category: StatusCategory;
  position: number;
  jira_status_id: string | null;
};
export type IssueCategory = { id: string; name: string; tone: string; position: number };
/** project_team: everyone on the project, named even for a contractor. */
export type TeamPerson = {
  id: string;
  name: string;
  avatar_url: string | null;
  project_role: 'lead' | 'contributor' | 'viewer';
};

export type Issue = {
  id: string;
  project_id: string;
  /** The key, MC-12 (tasks.code). */
  code: string | null;
  number: number | null;
  /** The Summary. */
  title: string;
  /** Plain text of the description: search reads it. Null on Jira rows. */
  description: string | null;
  status_id: string;
  issue_type: IssueType;
  /** A subtask's parent (story, task or bug). */
  parent_id: string | null;
  /** The epic a story, task or bug sits in. */
  epic_id: string | null;
  rank: string;
  labels: string[];
  priority: string;
  start_date: string | null;
  due_date: string | null;
  estimate_hrs: number;
  logged_hrs: number;
  reporter: string | null;
  category_id: string | null;
  team_id: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  jira_issue_id: string | null;
  jira_status: string | null;
  /** One assignee per work item, as in Jira. */
  assignee: string | null;
};

export const ISSUE_COLS =
  'id, project_id, code, number, title, description, status_id, issue_type, parent_id, epic_id, rank, labels, priority, start_date, due_date, estimate_hrs, logged_hrs, reporter, category_id, team_id, created_at, updated_at, resolved_at, jira_issue_id, jira_status, task_assignees(user_id)';

type IssueRow = Omit<Issue, 'assignee'> & {
  task_assignees?: { user_id: string }[] | { user_id: string } | null;
};
export const toIssue = ({ task_assignees, ...r }: IssueRow): Issue => ({
  ...r,
  labels: r.labels ?? [],
  estimate_hrs: Number(r.estimate_hrs ?? 0),
  logged_hrs: Number(r.logged_hrs ?? 0),
  assignee: (Array.isArray(task_assignees) ? task_assignees[0] : task_assignees)?.user_id ?? null,
});

/** Rank order. tasks.rank is C-collated printable ASCII, so plain < agrees with the database. */
export const byRank = (a: Pick<Issue, 'rank'>, b: Pick<Issue, 'rank'>): number =>
  a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0;

/** The work item's page within its project (spec §6.2). */
export const issueHref = (projectId: string, key: string | null): string =>
  `/project?id=${projectId}${key ? `&issue=${encodeURIComponent(key)}` : ''}`;

export type IssueRights = {
  /** Create, edit, move, assign: can_edit_project, and not read-only Jira. */
  edit: boolean;
  /** Workflow, categories, the team: can_lead_project, native projects only. */
  lead: boolean;
  /** Delete: the lead; never on a Jira-linked project (owner, Q2). */
  remove: boolean;
};

export type ProjectIssues = {
  state: 'loading' | 'ready' | 'failed';
  issues: Issue[];
  statuses: IssueStatus[];
  categories: IssueCategory[];
  team: TeamPerson[];
  /** Jira's board: a jira_links row, or any mirrored row still on it. */
  linked: boolean;
  jiraKey: string | null;
  /** Linked, and writing to Jira from here isn't on yet (JIRA_WRITE). */
  readOnly: boolean;
  /** Cards can be reordered within a column: always natively; on Jira until it refuses a rank (meta.rank_ok). */
  rankOk: boolean;
  rights: IssueRights;
  reload: () => Promise<void>;
  /** Hold live refreshes while a card is being dragged; they apply on drop. */
  setDragging: (on: boolean) => void;
  /** Change the local copy now (an optimistic move); the next load replaces it. */
  patchLocal: (id: string, patch: Partial<Issue>) => void;
};

/**
 * A project's work items, workflow, categories and team, live. One realtime
 * channel per project page (spec §6.2): tasks, project_statuses,
 * task_assignees and task_categories. A change reloads; while a drag is in
 * flight it waits, so a teammate's edit can't yank the card from under the
 * pointer, and lands on drop.
 */
export function useProjectIssues(projectId: string | null): ProjectIssues {
  const { perms, session } = useAuth();
  const uid = session?.user?.id ?? null;
  const [state, setState] = useState<ProjectIssues['state']>('loading');
  const [issues, setIssues] = useState<Issue[]>([]);
  const [statuses, setStatuses] = useState<IssueStatus[]>([]);
  const [categories, setCategories] = useState<IssueCategory[]>([]);
  const [team, setTeam] = useState<TeamPerson[]>([]);
  const [jiraKey, setJiraKey] = useState<string | null>(null);
  const [rankOk, setRankOk] = useState(true);
  const dragging = useRef(false);
  const pending = useRef(false);
  const ids = useRef(new Set<string>());

  const reload = useCallback(async () => {
    const sb = supabase;
    if (!sb || !projectId) return;
    const [t, s, c, tm, link] = await Promise.all([
      // Gone Jira issues keep their history in the table and stay off the page.
      sb
        .from('tasks')
        .select(ISSUE_COLS)
        .eq('project_id', projectId)
        .is('jira_gone_at', null)
        .order('rank'),
      sb
        .from('project_statuses')
        .select('id, name, category, position, jira_status_id')
        .eq('project_id', projectId)
        // ponytail: ties only from two statuses created at once; reorder_statuses renumbers.
        .order('position')
        .order('created_at'),
      sb
        .from('task_categories')
        .select('id, name, tone, position')
        .eq('project_id', projectId)
        .order('position'),
      sb.rpc('project_team', { p_project: projectId }),
      sb.from('jira_links').select('jira_key, meta').eq('project_id', projectId).maybeSingle(),
    ]);
    if (t.error || s.error) {
      setState('failed');
      return;
    }
    const list = ((t.data as unknown as IssueRow[]) ?? []).map(toIssue);
    ids.current = new Set(list.map((i) => i.id));
    setIssues(list);
    setStatuses((s.data as IssueStatus[]) ?? []);
    setCategories((c.data as IssueCategory[] | null) ?? []);
    setTeam(((tm as { data: unknown }).data as TeamPerson[] | null) ?? []);
    const l = link.data as { jira_key: string; meta?: { rank_ok?: boolean } | null } | null;
    setJiraKey(l?.jira_key ?? null);
    setRankOk(l?.meta?.rank_ok !== false);
    setState('ready');
  }, [projectId]);

  useEffect(() => {
    const sb = supabase;
    if (!sb || !projectId) return;
    void reload();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const changed = (taskId?: string) => {
      // task_assignees has no project_id to filter on: only ours count.
      if (taskId && !ids.current.has(taskId)) return;
      if (dragging.current) {
        pending.current = true;
        return;
      }
      clearTimeout(timer);
      timer = setTimeout(() => void reload(), 150);
    };
    const mine = { schema: 'public', filter: `project_id=eq.${projectId}` } as const;
    const ch = sb
      .channel(`project:${projectId}`)
      .on('postgres_changes', { event: '*', table: 'tasks', ...mine }, () => changed())
      .on('postgres_changes', { event: '*', table: 'project_statuses', ...mine }, () => changed())
      .on('postgres_changes', { event: '*', table: 'task_categories', ...mine }, () => changed())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_assignees' }, (e) => {
        const row = (e.new ?? e.old) as { task_id?: string } | undefined;
        changed(row?.task_id ?? '');
      })
      .subscribe();
    return () => {
      clearTimeout(timer);
      void sb.removeChannel(ch);
    };
  }, [projectId, reload]);

  const setDragging = useCallback(
    (on: boolean) => {
      dragging.current = on;
      if (!on && pending.current) {
        pending.current = false;
        void reload();
      }
    },
    [reload],
  );
  const patchLocal = useCallback(
    (id: string, patch: Partial<Issue>) =>
      setIssues((xs) => xs.map((i) => (i.id === id ? { ...i, ...patch } : i))),
    [],
  );

  const linked = jiraKey != null || issues.some((i) => i.jira_issue_id != null);
  const readOnly = linked && !JIRA_WRITE;
  const rights = useMemo<IssueRights>(() => {
    const role = team.find((p) => p.id === uid)?.project_role;
    const full = can(perms, 'projects', 'full');
    const editor = can(perms, 'projects', 'edit');
    const lead = full || (editor && role === 'lead');
    return {
      edit: !readOnly && (full || (editor && (role === 'lead' || role === 'contributor'))),
      lead: lead && !linked,
      remove: lead && !linked,
    };
  }, [perms, uid, team, readOnly, linked]);

  return {
    state,
    issues,
    statuses,
    categories,
    team,
    linked,
    jiraKey,
    readOnly,
    rankOk: !linked || rankOk,
    rights,
    reload,
    setDragging,
    patchLocal,
  };
}

// ── Writes ───────────────────────────────────────────────────────────────────

/**
 * `warning`: done, with something to say (Jira moved it but kept its order).
 * `pending`: Jira took it, and the minute's pull brings it here; never retry.
 */
export type ActionResult<T = undefined> =
  { ok: true; data?: T; warning?: string; pending?: boolean } | { ok: false; error: string };

/** The fields a signed-in write may set (tasks_b10_guard refuses the rest). */
export type IssuePatch = Partial<
  Pick<
    Issue,
    | 'title'
    | 'description'
    | 'status_id'
    | 'issue_type'
    | 'parent_id'
    | 'epic_id'
    | 'labels'
    | 'priority'
    | 'start_date'
    | 'due_date'
    | 'estimate_hrs'
    | 'reporter'
    | 'category_id'
    | 'team_id'
    | 'rank'
  >
> & {
  /** Jira's document format; the database keeps `description` as its plain text. */
  description_adf?: unknown;
  assignee?: string | null;
};
/** `rank` places it (the board's "+" between two cards); omitted, it goes to the bottom. */
export type NewIssue = IssuePatch & { title: string; rank?: string };

export const READ_ONLY_MESSAGE =
  'This project’s work items are managed in Jira. Change them there; they update here.';

const refused = (r: Awaited<ReturnType<typeof write>>, what: string): ActionResult => {
  if (r.ok) return { ok: true };
  return {
    ok: false,
    error: r.reason === 'refused' ? `You can’t ${what} this work item.` : r.error.message,
  };
};

/** What jira-edit answers; it always answers HTTP 200. */
type JiraAnswer = {
  ok?: boolean;
  error?: string;
  task?: IssueRow;
  url?: string;
  warning?: string;
  pending?: boolean;
};

/**
 * A Jira-linked item's write: the jira-edit function, with the caller's JWT
 * (spec §5.3, §5.4). {ok, task} once Jira has it and it is read back, {ok,
 * pending} when Jira took it but the read-back didn't finish, or {ok:false,
 * error} in words to show. `shape` picks what the caller needs from the answer.
 */
export const jiraEdit = async <T = undefined>(
  op: string,
  projectId: string,
  task: string | null,
  args: Record<string, unknown>,
  shape?: (answer: JiraAnswer) => T | undefined,
): Promise<ActionResult<T>> => {
  if (!JIRA_WRITE || !supabase) return { ok: false, error: READ_ONLY_MESSAGE };
  const { data, error } = await supabase.functions.invoke('jira-edit', {
    body: { op, project: projectId, task, args },
  });
  const res = data as JiraAnswer | null;
  if (error || !res?.ok) {
    return { ok: false, error: res?.error ?? error?.message ?? 'Jira didn’t answer.' };
  }
  return {
    ok: true,
    data: shape?.(res),
    ...(res.warning ? { warning: res.warning } : {}),
    ...(res.pending ? { pending: true } : {}),
  };
};

/**
 * Open a Jira file (C3): jira-edit hands back a short-lived Atlassian link,
 * and the browser fetches it from Atlassian; the bytes never pass through
 * Hyprflow. The tab opens at the click, so a popup blocker lets it through,
 * and goes to the link once it comes. null when it opened, else what to say.
 */
export const openJiraFile = async (
  projectId: string,
  taskId: string,
  attachmentId: string,
): Promise<string | null> => {
  const tab = window.open('about:blank', '_blank');
  if (tab) tab.opener = null;
  const r = await jiraEdit(
    'file.url',
    projectId,
    taskId,
    { attachment: attachmentId },
    (a) => a.url,
  );
  if (!r.ok || !r.data) {
    tab?.close();
    return r.ok ? 'Jira didn’t give a link to that file.' : r.error;
  }
  if (tab) tab.location.href = r.data;
  else window.location.assign(r.data);
  return null;
};

/** One assignee per work item: replace whoever is on it, or clear it. */
const setAssignee = async (taskId: string, userId: string | null): Promise<ActionResult> => {
  if (!supabase) return { ok: false, error: 'Not connected.' };
  const { error } = userId
    ? await supabase
        .from('task_assignees')
        .upsert({ task_id: taskId, user_id: userId }, { onConflict: 'task_id' })
    : await supabase.from('task_assignees').delete().eq('task_id', taskId);
  return error ? { ok: false, error: error.message } : { ok: true };
};

/**
 * The four writes for one project. Native ones go to PostgREST and the board's
 * RPCs; a linked project's go to jira-edit (spec §5.3) once JIRA_WRITE is on,
 * and are refused with READ_ONLY_MESSAGE until then.
 */
export const issueActions = (projectId: string, linked: boolean) => ({
  /** To a status, between two neighbours in it (both null: the bottom). Returns the new rank. */
  move: async (
    issue: Pick<Issue, 'id'>,
    statusId: string,
    prevId: string | null = null,
    nextId: string | null = null,
  ): Promise<ActionResult<string>> => {
    if (linked)
      return jiraEdit(
        'move',
        projectId,
        issue.id,
        { status: statusId, prev: prevId, next: nextId },
        (a) => a.task?.rank,
      );
    if (!supabase) return { ok: false, error: 'Not connected.' };
    const { data, error } = await supabase.rpc('move_issue', {
      p_task: issue.id,
      p_status: statusId,
      p_prev: prevId,
      p_next: nextId,
    });
    return error ? { ok: false, error: error.message } : { ok: true, data: data as string };
  },

  update: async (issue: Pick<Issue, 'id'>, patch: IssuePatch): Promise<ActionResult> => {
    if (linked) {
      // Jira changes a status only through its workflow: that part is a move.
      const { status_id, ...rest } = patch;
      if (status_id) {
        const moved = await jiraEdit('move', projectId, issue.id, { status: status_id });
        if (!moved.ok || !Object.keys(rest).length) return moved;
      }
      return jiraEdit('update', projectId, issue.id, rest);
    }
    if (!supabase) return { ok: false, error: 'Not connected.' };
    const { assignee, ...fields } = patch;
    if (Object.keys(fields).length) {
      const r = refused(
        await write(supabase.from('tasks').update(fields).eq('id', issue.id)),
        'change',
      );
      if (!r.ok) return r;
    }
    return assignee !== undefined ? setAssignee(issue.id, assignee) : { ok: true };
  },

  create: async (fields: NewIssue): Promise<ActionResult<Issue>> => {
    // The request id lets Jira's copy be found, not made twice, if the answer is lost.
    if (linked)
      return jiraEdit(
        'create',
        projectId,
        null,
        { request: crypto.randomUUID(), ...fields },
        (a) => (a.task ? toIssue(a.task) : undefined),
      );
    if (!supabase) return { ok: false, error: 'Not connected.' };
    const { assignee, ...row } = fields;
    const { data, error } = await supabase
      .from('tasks')
      .insert({ ...row, project_id: projectId })
      .select(ISSUE_COLS)
      .single();
    if (error) return { ok: false, error: error.message };
    const issue = toIssue(data as unknown as IssueRow);
    if (assignee) {
      const a = await setAssignee(issue.id, assignee);
      if (!a.ok) return { ok: false, error: `Created ${issue.code ?? ''}, but: ${a.error}` };
      issue.assignee = assignee;
    }
    return { ok: true, data: issue };
  },

  /** Jira to native, one way (spec §5.7): org:full, through jira-edit (C2). */
  switchToNative: (): Promise<ActionResult> => jiraEdit('switch', projectId, null, {}),

  remove: async (issue: Pick<Issue, 'id'>): Promise<ActionResult> => {
    // Never delete in a client's Jira from here (owner, Q2).
    if (linked) return { ok: false, error: 'Delete this work item in Jira.' };
    if (!supabase) return { ok: false, error: 'Not connected.' };
    return refused(await write(supabase.from('tasks').delete().eq('id', issue.id)), 'delete');
  },
});
export type IssueActions = ReturnType<typeof issueActions>;
